/* eslint-disable import/no-cycle */
import { IAddress } from './Address';
import { IJob } from './Job';
import { IOrganization } from './Organization';
import { ISiteSettings } from './Site';
import { ISubmittedBy, IUserWithDetails } from './User';

export enum DisbursementMethod {
  INSTANT = 'instant',
  PAYROLL = 'payroll',
}

export enum EmployeeActionRequiredType {
  EMPLOYER_APPROVAL = 'employer_approval',
  INVITE_EXPIRED = 'invite_expired',
  INVITE_REQUIRED = 'invite_required',
  INVITE_SEND_FAILURE = 'invite_send_failure',
  NONE = 'null',
}

export enum EmployeeApprovalValueFormat {
  EMAIL = 'email',
  MOBILE = 'mobile_number',
  STRING = 'string',
}

export enum EmployeeStatus {
  ACTION_REQUIRED = 'action_required',
  APPROVED = 'approved',
  DISMISSED = 'dismissed',
  INVITED = 'invited',
}

export enum EmployeeType {
  ADMIN = 'admin',
  USER = 'user',
}

export enum EventActionTypes {
  ACCEPT_INVITE = 'accept_invite',
  CREATE = 'create',
  DELETE = 'delete',
  UPDATE = 'update',
}

enum MatchConditions {
  EXACT = 'exact',
  FUZZY = 'fuzzy',
}

export enum SiteRole {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
  MANAGER = 'manager',
}

export interface IEmployeeApprovalFailure {
  employeeValue: string;
  field: string;
  matchCondition: MatchConditions;
  userValue?: string;
  valueFormat: EmployeeApprovalValueFormat;
}

export interface IBaseEmployee {
  id: string;
  name: string;
  type: EmployeeType;
  status: EmployeeStatus;
  approvalFailures?: IEmployeeApprovalFailure[];
  integrations?: IEmployeeIntegration[];
  organization?: IOrganization;
  sites: IEmployeeSite[];
  user?: IUserWithDetails;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface IEmployee extends IBaseEmployee {
  actionRequired?: EmployeeActionRequiredType;
  dismissReason?: string;
  firstName: string;
  lastName: string;
  inviteRecipient?: string;
  statusHistory?: IEmployeeStatusHistory[];
}

export interface IEmployeeAcceptance {
  code?: string;
  enabled: boolean;
}

export interface IEmployeeIntegration {
  id: string;
  siteId: string;
  type: string;
}

export interface IEmployeeSite {
  acceptance?: IEmployeeAcceptance;
  address?: IAddress;
  disbursementMethod: DisbursementMethod;
  dismissReason?: string;
  employeeDisplayName?: string;
  employeeId?: string;
  id: string;
  job: IJob;
  name: string;
  legalName?: string;
  role: SiteRole;
  settings?: ISiteSettings;
  salesForce?: string;
  status: EmployeeStatus;
  createdAt?: string;
  updatedAt?: string;
}

interface IEmployeeStatusHistory {
  action: EventActionTypes;
  changedBy: ISubmittedBy;
  status: EmployeeStatus;
  createdAt: string;
}

export interface IPermission {
  status: PermissionStatus;
  sites: string[];
}

export interface ICreateEmployee {
  firstName: string;
  lastName: string;
  type: EmployeeType;
  inviteRecipient: string;
  organizationId: string;
  sites?: ICreateEmployeeSite[];
  integrations?: IEmployeeIntegration[];
}

export interface IUpdateEmployee {
  firstName?: string;
  lastName?: string;
  inviteRecipient?: string;
  status?: EmployeeStatus;
  type?: EmployeeType;
  dismissReason?: string;
  integrations?: IEmployeeIntegration[];
  permissions?: {
    paymentApproval?: IPermission;
  };
}

export interface ICreateEmployeeSite {
  id: string;
  role: SiteRole;
  job?: IJob;
  disbursementMethod?: DisbursementMethod;
  employeeDisplayName?: string;
  employeeId?: string;
  acceptance?: IEmployeeAcceptance;
}

export interface IUpdateEmployeeSite {
  role?: SiteRole;
  job?: IJob;
  status?: EmployeeStatus;
  disbursementMethod?: DisbursementMethod;
  employeeDisplayName?: string;
  employeeId?: string;
  acceptance?: IEmployeeAcceptance;
  dismissReason?: string;
}

export enum AffiliationType {
  SITES = 'Sites',
  ORGANIZATIONS = 'Organizations',
}

export enum EmployeeReviewFieldKey {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  DATE_OF_BIRTH = 'dateOfBirth',
  IDENTITY_RECIPIENT = 'invite_recipient',
}

export interface IEmployeeReviewField {
  user: string;
  employee: string;
}

export enum EmployeeRole {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
  MANAGER = 'manager',
  SITE_ADMIN = 'siteadmin',
}

export interface IPayee extends IEmployee {
  id: string;
  disbursementMethod: DisbursementMethod;
  tipAmount?: number;
  holdbackAmount?: number;
  job?: IJob;
  note?: string;
}
